import {cn} from '@tempmee/core'
import {OutlineUploadIcon} from '@tempmee/react-icons'
import {type ChangeEvent, type DragEvent, type ReactNode, forwardRef, useId, useState} from 'react'
import {DropzoneVariantsFactory} from './Variants'

export interface FileDropzoneProps {
  /** Small label above the box (e.g. "Upload file"). Omit to hide. */
  label?: ReactNode
  /** The highlighted, clickable word that opens the file picker. @default 'Link' */
  browseLabel?: ReactNode
  /** Helper text below the prompt (e.g. "Accepts .xlsx and .xls files"). */
  hint?: ReactNode
  /** Error state — error tint + red helper text. */
  error?: boolean
  /** Message shown in place of `hint` while `error` is set. */
  errorText?: ReactNode
  /** Disable the picker and drag-and-drop. */
  disabled?: boolean
  /** Allow selecting more than one file. */
  multiple?: boolean
  /** Native input `accept` filter (e.g. ".xlsx,.xls"). */
  accept?: string
  /** Fired with the chosen or dropped files. */
  onSelectFiles?: (files: File[]) => void
  /** Override the upload icon (rendered at 22px). */
  icon?: ReactNode
  /** Class applied to the dashed box. */
  className?: string
  /** Wrapper id; the inner input gets `${id}-input`. */
  id?: string
}

/**
 * Dashed file-select dropzone. Click anywhere (or the highlighted link) to open
 * the native picker, or drag files onto the box. Purely presents the selection
 * UI — the consumer handles the chosen files via `onSelectFiles`.
 */
export const FileDropzone = forwardRef<HTMLDivElement, FileDropzoneProps>(
  (
    {
      label,
      browseLabel = 'Link',
      hint,
      error = false,
      errorText,
      disabled = false,
      multiple = false,
      accept,
      onSelectFiles,
      icon = <OutlineUploadIcon />,
      className,
      id,
    },
    ref,
  ) => {
    const reactId = useId()
    const inputId = `${id ?? reactId}-input`
    const [active, setActive] = useState(false)

    const state = error ? 'error' : active ? 'active' : 'default'

    const emit = (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return
      onSelectFiles?.(Array.from(fileList))
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      emit(e.target.files)
      // Allow re-selecting the same file.
      e.target.value = ''
    }

    const handleDragOver = (e: DragEvent<HTMLLabelElement>) => {
      if (disabled) return
      e.preventDefault()
      setActive(true)
    }

    const handleDragLeave = (e: DragEvent<HTMLLabelElement>) => {
      if (disabled) return
      e.preventDefault()
      setActive(false)
    }

    const handleDrop = (e: DragEvent<HTMLLabelElement>) => {
      if (disabled) return
      e.preventDefault()
      setActive(false)
      emit(e.dataTransfer.files)
    }

    return (
      <div ref={ref} id={id} className={cn('flex w-full flex-col gap-2', className)}>
        {label != null && (
          <span className="text-xs font-light tracking-[0.15px] text-text-secondary">{label}</span>
        )}
        <label
          htmlFor={inputId}
          data-dropzone=""
          data-state={state}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={DropzoneVariantsFactory({state, disabled})}
        >
          <input
            id={inputId}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            onChange={handleChange}
            className="sr-only"
          />
          <span className="flex size-10 items-center justify-center text-text-primary [&_svg]:size-[22px]">
            {icon}
          </span>
          <span className="flex items-center gap-1 text-base">
            <span className="text-primary underline underline-offset-2">{browseLabel}</span>
            <span className="font-bold leading-7 text-text-primary">or drag and drop</span>
          </span>
          {(error ? errorText : hint) != null && (
            <span className={cn('text-sm', error ? 'text-error' : 'text-text-secondary')}>
              {error ? errorText : hint}
            </span>
          )}
        </label>
      </div>
    )
  },
)
FileDropzone.displayName = 'FileDropzone'
